import { useMemo } from "react"
import { Button, Card, Space } from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { useCrud } from "@/hooks/useCrud"
import { ID } from "@/types/common"
import { Toast } from "@/components/Toast"
import fileAssetApi from "./api"
import FileAssetModal from "./components/FileAssetModal"
import FileAssetTable from "./components/FileAssetTable"
import { FileAsset, FileAssetFormData } from "./types"

const FileAssetManagement = () => {
  const api = useMemo(
    () => ({
      getList: (params?: { page?: number; pageSize?: number }) =>
        fileAssetApi.getList(params),
      create: fileAssetApi.create,
      update: (id: ID, data: FileAssetFormData | Partial<FileAssetFormData>) =>
        fileAssetApi.update(id, data as FileAssetFormData),
      delete: fileAssetApi.delete,
    }),
    [],
  )

  const {
    items,
    loading,
    opLoading,
    isModalOpen,
    editingItem,
    selectedIds,
    setSelectedIds,
    pagination,
    handleAdd,
    handleEdit,
    handleSave,
    handleDelete,
    handleBatchDelete,
    handleCloseModal,
    handlePageChange,
  } = useCrud<FileAsset, FileAssetFormData>({
    api,
    entityName: "文件",
    pagination: {
      enabled: true,
      pageSize: 10,
    },
  })

  const handleDownload = async (item: FileAsset) => {
    try {
      await fileAssetApi.download(item)
    } catch (error: any) {
      Toast.error(error.message || "下载文件失败")
    }
  }

  return (
    <div className="p-6">
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <span className="text-gray-500">共 {pagination.total} 个文件</span>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              loading={opLoading}
            >
              上传文件
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={selectedIds.length === 0}
              loading={opLoading}
            >
              批量删除 {selectedIds.length > 0 && `(${selectedIds.length})`}
            </Button>
          </Space>
        </div>

        <FileAssetTable
          items={items}
          loading={loading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDownload={handleDownload}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          onPageChange={handlePageChange}
        />
      </Card>

      <FileAssetModal
        isOpen={isModalOpen}
        fileAsset={editingItem}
        loading={opLoading}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
    </div>
  )
}

export default FileAssetManagement
